import { IconLabelSize } from './constants';

// Shared icon utility constants
export const IconShapeDefaults = { viewBox: '0 0 24 24', d: '' };

// Helper to convert icon name to slug for URL (shared by IconElement and icon labels)
export function toIconSlug(iconName: string): string {
    const fallback = 'production_facility';
    if (!iconName) {
        return fallback;
    }
    const lastSegment = iconName.split('.').pop() ?? iconName;
    const normalized = lastSegment.trim().toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '');
    return normalized || fallback;
}

// Fetch icon shape data from server (shared by IconElement and icon labels)
export async function fetchIconShape(iconName: string): Promise<{ viewBox: string; d: string }> {
    const slug = toIconSlug(iconName);

    // URL construction is inside the try on purpose. Outside a browser there is no document to
    // resolve against, and `new URL(relative, '/')` throws — which, because IconElement.initialize
    // calls this fire-and-forget, surfaced as an unhandled rejection that took down any headless
    // suite merely constructing an icon. There are no icons to fetch without a document, so the
    // defaults below are the correct answer rather than a swallowed failure.
    try {
        const base = typeof document !== 'undefined' ? document.baseURI : '/';
        const url = new URL(`icons/flow-icons/${slug}.svg`, base).toString();

        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Failed to fetch icon: ${url}`);
        }
        const svgText = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(svgText, 'image/svg+xml');
        const svgEl = doc.querySelector('svg');
        const pathEl = doc.querySelector('path');
        const viewBox = svgEl?.getAttribute('viewBox') || IconShapeDefaults.viewBox;
        const d = pathEl?.getAttribute('d') || IconShapeDefaults.d;
        return { viewBox, d };
    } catch {
        // Fallback to production_facility
        if (slug !== 'production_facility') {
            return fetchIconShape('Production Facility');
        }
        return IconShapeDefaults;
    }
}

// Create icon label markup for a link
export function createIconLabelMarkup(iconName: string, pathD: string, viewBox: string): object {
    return {
        markup: [
            {
                tagName: 'rect',
                selector: 'iconBackground',
            },
            {
                tagName: 'svg',
                selector: 'iconSvg',
                children: [
                    {
                        tagName: 'path',
                        selector: 'iconPath',
                    },
                ],
            },
        ],
        attrs: {
            iconBackground: {
                width: IconLabelSize.width + 4,
                height: IconLabelSize.height + 4,
                x: -(IconLabelSize.width + 4) / 2,
                y: -(IconLabelSize.height + 4) / 2,
                fill: '#FFFFFF',
                stroke: '#D0D0CE',
                strokeWidth: 1,
                rx: 2,
                ry: 2,
                cursor: 'pointer',
            },
            iconSvg: {
                width: IconLabelSize.width,
                height: IconLabelSize.height,
                x: -IconLabelSize.width / 2,
                y: -IconLabelSize.height / 2,
                viewBox: viewBox,
                preserveAspectRatio: 'xMidYMid meet',
            },
            iconPath: {
                d: pathD,
                fill: '#000000',
                stroke: '#000000',
                strokeWidth: 0.5,
                'pointer-events': 'none',
            },
        },
        // Store icon metadata for later retrieval
        iconName: iconName,
        isIconLabel: true,
    };
}

// Check if a label is an icon label
export function isIconLabel(label: any): boolean {
    return label?.isIconLabel === true || label?.iconName != null;
}
